/**
 * apps/web :: sw-registration
 *
 * Registers `/sw.js` at root scope on app boot. Called exactly once from
 * `main.tsx` after the React tree mounts.
 *
 * `/sw.js` is emitted at build time by VitePWA's `injectManifest` step from
 * the TypeScript source at `src/sw/push-and-precache.ts`. That single worker
 * handles both push delivery (ADR-0008) and offline precache / runtime
 * caching (Workbox). See `vite.config.ts` for the pipeline details.
 *
 * Guardrails:
 *   - Requires a secure context (`window.isSecureContext`); browsers refuse
 *     to register service workers on plain http outside of localhost.
 *   - Gracefully no-ops when `navigator.serviceWorker` is undefined (older
 *     browsers, Safari on iOS < 16.4 PWA).
 *   - `updateViaCache: 'none'` so browsers always revalidate the SW script
 *     and new deploys activate on next navigation (per PR #81 guidance).
 *   - Failures never throw into the app — they're logged with a
 *     non-secret, user-safe warning.
 */

export function shouldRegisterServiceWorker(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  // Secure context is required by all evergreen browsers. `localhost` is
  // treated as secure for this purpose even over http.
  return window.isSecureContext === true;
}

export async function registerAppServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!shouldRegisterServiceWorker()) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    return registration;
  } catch (err) {
    // Don't leak stack traces into production UX; this is best-effort.
    // `console.warn` is intentional — no PII or keys flow through here.
    // eslint-disable-next-line no-console
    console.warn('[sw-registration] service worker registration failed', err);
    return null;
  }
}
